import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  DEFAULT_PRODUCT_EXPORT_SETTINGS,
  DEFAULT_PRODUCT_INTRO_SETTINGS,
  DEFAULT_PRODUCT_MODE_SETTINGS,
  PRODUCT_EXPORT_STORAGE_KEY,
  PRODUCT_INTRO_STORAGE_KEY,
  PRODUCT_MODE_MAX_FIELDS,
} from "../constants";
import {
  getProductImageColumns,
  getProductFieldColumns,
  getProductRecordColumns,
  detectPriceColumn,
  detectTitleColumn,
  detectUrlColumn,
  getRowImageCandidates,
  resolveTileImage,
  loadProductModeSettings,
  saveProductModeSettings,
  loadAnimationSettings,
  saveAnimationSettings,
  sanitizeAnimationSettings,
} from "../utils/productMode";

// A per-device on/off + duration pair, saved as it changes.
const useDeviceAnimationSettings = (storageKey, defaults) => {
  const [settings, setSettings] = useState(() => loadAnimationSettings(storageKey, defaults));
  useEffect(() => {
    saveAnimationSettings(storageKey, settings);
  }, [storageKey, settings]);
  const update = useCallback(
    (patch) => setSettings((prev) => sanitizeAnimationSettings({ ...prev, ...patch }, defaults)),
    [defaults]
  );
  return [settings, update];
};

/**
 * Product mode state for the cloud table: the mobile-only view of the rows as
 * a product sheet (or an image grid), with per-table settings that survive a
 * reload.
 *
 * Settings live in a map keyed by table id and are only written back for a table
 * the user actually changed, so switching tables never copies one table's
 * settings onto another.
 *
 * @param {Object} params
 * @param {string|null} params.tableId
 * @param {Array} params.columns Raw table columns
 * @param {Array} params.data Loaded rows (used to pick defaults)
 * @param {Object} params.filters Table filters (debug columns visibility)
 * @param {boolean} params.isMobile Whether the viewport is a phone
 */
export const useProductMode = ({ tableId, columns, data, filters, isMobile }) => {
  const [settingsByTable, setSettingsByTable] = useState({});
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const storedSettings = useMemo(
    () => settingsByTable[tableId] || loadProductModeSettings(tableId),
    [settingsByTable, tableId]
  );

  useEffect(() => {
    const changed = settingsByTable[tableId];
    if (tableId && changed) saveProductModeSettings(tableId, changed);
  }, [settingsByTable, tableId]);

  const updateSettings = useCallback(
    (patch) => {
      if (!tableId) return;
      setSettingsByTable((prev) => {
        const current = prev[tableId] || loadProductModeSettings(tableId);
        const next = typeof patch === "function" ? patch(current) : { ...current, ...patch };
        return next === current ? prev : { ...prev, [tableId]: next };
      });
    },
    [tableId]
  );

  // Back to defaults, but stay in product mode — the sheet that offers Reset is
  // only reachable from there.
  const resetSettings = useCallback(
    () => updateSettings((current) => ({ ...DEFAULT_PRODUCT_MODE_SETTINGS, enabled: current.enabled })),
    [updateSettings]
  );

  const showDebugColumns = Boolean(filters?.showDebugColumns);
  const imageColumns = useMemo(() => getProductImageColumns(columns, { showDebugColumns }), [columns, showDebugColumns]);
  const fieldColumns = useMemo(() => getProductFieldColumns(columns, { showDebugColumns }), [columns, showDebugColumns]);
  const recordColumns = useMemo(() => getProductRecordColumns(columns, { showDebugColumns }), [columns, showDebugColumns]);
  const priceColumn = useMemo(() => detectPriceColumn(fieldColumns, data), [fieldColumns, data]);
  const titleColumn = useMemo(() => detectTitleColumn(fieldColumns, data), [fieldColumns, data]);
  const urlColumn = useMemo(() => detectUrlColumn(fieldColumns, data), [fieldColumns, data]);

  // A stored image column may have been deleted since; fall back to all of them.
  const imageColumnId =
    storedSettings.imageColumnId != null && imageColumns.some((col) => col.id === storedSettings.imageColumnId)
      ? storedSettings.imageColumnId
      : null;
  const settings = useMemo(() => ({ ...storedSettings, imageColumnId }), [storedSettings, imageColumnId]);

  // What a row shows before the user picks fields: the sheet names the
  // product, links to it and prices it; a tile only has room for the price.
  const autoFieldIds = useMemo(() => {
    const picks = settings.layout === "grid" ? [priceColumn] : [titleColumn, urlColumn, priceColumn];
    const ids = [];
    for (const col of picks) {
      if (col && !ids.includes(col.id)) ids.push(col.id);
    }
    return ids;
  }, [settings.layout, titleColumn, urlColumn, priceColumn]);

  const activeFields = useMemo(
    () =>
      (settings.fieldIds ?? autoFieldIds)
        .map((id) => fieldColumns.find((col) => col.id === id))
        .filter(Boolean)
        .slice(0, PRODUCT_MODE_MAX_FIELDS),
    [settings.fieldIds, autoFieldIds, fieldColumns]
  );

  const canActivate = Boolean(isMobile && imageColumns.length > 0);
  const isActive = canActivate && settings.enabled;

  const toggle = useCallback(() => updateSettings((current) => ({ ...current, enabled: !current.enabled })), [updateSettings]);

  const setCover = useCallback(
    (rowId, url) => {
      if (rowId == null || !url) return;
      updateSettings((current) => ({ ...current, covers: { ...current.covers, [rowId]: url } }));
    },
    [updateSettings]
  );

  const resolveTile = useCallback((row) => resolveTileImage(row, settings, imageColumns), [settings, imageColumns]);
  const getRowImages = useCallback((row) => getRowImageCandidates(row, imageColumns), [imageColumns]);

  // How many "1st / 2nd / …" positions make sense for the chosen source.
  const maxImagesPerRow = useMemo(
    () => (data || []).reduce((max, row) => Math.max(max, getRowImageCandidates(row, imageColumns, imageColumnId).length), 0),
    [data, imageColumns, imageColumnId]
  );

  const openSettings = useCallback(() => setIsSettingsOpen(true), []);
  const closeSettings = useCallback(() => setIsSettingsOpen(false), []);

  useEffect(() => {
    if (!isActive) setIsSettingsOpen(false);
  }, [isActive]);

  // Intro animation. Its settings are per device; a run is keyed by the time
  // it was requested so a replay always remounts the overlay.
  const [introSettings, updateIntroSettings] = useDeviceAnimationSettings(PRODUCT_INTRO_STORAGE_KEY, DEFAULT_PRODUCT_INTRO_SETTINGS);

  const [introRun, setIntroRun] = useState(0);
  // Bumped as an intro leaves, so the view replays its row reveal underneath
  // the fading overlay.
  const [introEpoch, setIntroEpoch] = useState(0);

  const playIntro = useCallback(() => {
    setIsSettingsOpen(false);
    setIntroRun(Date.now());
  }, []);
  const leaveIntro = useCallback(() => setIntroEpoch((n) => n + 1), []);
  const finishIntro = useCallback(() => setIntroRun(0), []);

  // Play when product mode opens — the toggle, or a load with it already on.
  const wasActiveRef = useRef(false);
  useEffect(() => {
    if (isActive && !wasActiveRef.current && introSettings.enabled) setIntroRun(Date.now());
    if (!isActive) setIntroRun(0);
    wasActiveRef.current = isActive;
  }, [isActive, introSettings.enabled]);

  // Export animation (the export card). The view wraps the Export menu's
  // handlers: while the card is armed, a file export is handed to playExport
  // as `after` and only runs once the card is gone, so the browser's download
  // prompt follows the confirmation instead of cutting into it.
  const [exportSettings, updateExportSettings] = useDeviceAnimationSettings(PRODUCT_EXPORT_STORAGE_KEY, DEFAULT_PRODUCT_EXPORT_SETTINGS);
  const exportAnimationArmed = isActive && exportSettings.enabled;
  const [exportRun, setExportRun] = useState(null); // { key, format }
  const exportAfterRef = useRef(null);

  const runPendingExport = useCallback(() => {
    const after = exportAfterRef.current;
    exportAfterRef.current = null;
    if (after) after();
  }, []);

  const playExport = useCallback(
    (format, after = null) => {
      // A run that still holds a deferred export must not lose it.
      runPendingExport();
      setIsSettingsOpen(false);
      exportAfterRef.current = typeof after === "function" ? after : null;
      setExportRun({ key: Date.now(), format: typeof format === "string" && format ? format : "csv" });
    },
    [runPendingExport]
  );
  const finishExport = useCallback(() => {
    setExportRun(null);
    runPendingExport();
  }, [runPendingExport]);

  // Leaving product mode mid-card still delivers the export.
  useEffect(() => {
    if (!isActive) {
      setExportRun(null);
      runPendingExport();
    }
  }, [isActive, runPendingExport]);

  return {
    isActive,
    canActivate,
    isEnabled: settings.enabled,
    toggle,
    settings,
    updateSettings,
    resetSettings,
    imageColumns,
    fieldColumns,
    recordColumns,
    activeFields,
    priceColumn,
    maxImagesPerRow,
    resolveTile,
    getRowImages,
    setCover,
    isSettingsOpen,
    openSettings,
    closeSettings,
    introSettings,
    updateIntroSettings,
    introRun,
    introEpoch,
    playIntro,
    leaveIntro,
    finishIntro,
    exportSettings,
    updateExportSettings,
    exportAnimationArmed,
    exportRun,
    playExport,
    finishExport,
  };
};
